import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  dropItem, getOrCreateDraft, loadDraftItems, putItem, submitBatch,
} from './api';
import type { BatchItem, Card, ItemKind } from '../types';

export const keyOf = (unitId: string, locale: string) => `${unitId}@${locale}`;

/** Mirrors how fetchCards groups rows: deck prompts collapse to their prompt
 *  id, everything else stands alone. */
export function cardKeyFor(unitId: string, locale: string) {
  const [kind, promptId] = unitId.split('/');
  return kind === 'deck' && promptId ? `${promptId}@${locale}` : `${unitId}@${locale}`;
}

export type DecidePayload = { values?: Map<string, string>; note?: string };

/**
 * The member's cart, as one hook.
 *
 * Lifted out of the workspace so focus mode could share it: a decision made in
 * either place lands in the same draft batch, and the count in the top bar is
 * the same count wherever you are standing.
 */
export function useBatch(memberId: string) {
  const [batchId, setBatchId] = useState<string | null>(null);
  const [items, setItems] = useState<BatchItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // The cart is a draft batch row, so a closed tab never loses a session's work.
  useEffect(() => {
    let alive = true;
    getOrCreateDraft(memberId)
      .then(async (id) => {
        if (!alive) return;
        setBatchId(id);
        setItems(await loadDraftItems(id));
      })
      .catch((e) => alive && setError(e.message));
    return () => { alive = false; };
  }, [memberId]);

  // Keyed by card: a deck prompt produces an item for its question and another
  // for its instruction, and the card needs to see both to describe itself.
  const decisions = useMemo(() => {
    const m = new Map<string, BatchItem[]>();
    for (const i of items) {
      const k = cardKeyFor(i.unit_id, i.locale);
      (m.get(k) ?? m.set(k, []).get(k)!).push(i);
    }
    return m;
  }, [items]);

  const decide = useCallback(async (card: Card, kind: ItemKind, payload: DecidePayload) => {
    if (!batchId) return;

    // One card can yield several items. Saving a deck prompt whose question
    // changed but whose instruction did not records a fix for the first and a
    // confirmation for the second, so reading the whole card counts.
    // A question is one per card, not one per field: it hangs off the prompt,
    // and filing it twice would show the owner the same sentence twice and
    // post it twice into the thread.
    const built: BatchItem[] = kind === 'question'
      ? card.parts.slice(0, 1).map((p) => ({
          unit_id: p.unit_id, locale: p.locale, kind: 'question' as const,
          from_text: null, to_text: null, note: payload.note ?? '',
        }))
      : card.parts.map((p) => {
          const next = payload.values?.get(p.unit_id) ?? p.value ?? '';
          const changed = next !== (p.value ?? '');
          return {
            unit_id: p.unit_id, locale: p.locale,
            kind: (changed ? 'fix' : 'ok') as ItemKind,
            from_text: changed ? p.value : null,
            to_text: changed ? next : null,
            note: null,
          };
        });

    const keys = new Set(built.map((b) => keyOf(b.unit_id, b.locale)));
    setItems((cur) => [...cur.filter((i) => !keys.has(keyOf(i.unit_id, i.locale))), ...built]);
    try {
      const saved = await Promise.all(built.map((b) => putItem(batchId, b)));
      setItems((cur) => [...cur.filter((i) => !keys.has(keyOf(i.unit_id, i.locale))), ...saved]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setItems((cur) => cur.filter((i) => !keys.has(keyOf(i.unit_id, i.locale))));
    }
  }, [batchId]);

  const undo = useCallback(async (card: Card) => {
    if (!batchId) return;
    const keys = new Set(card.parts.map((p) => keyOf(p.unit_id, p.locale)));
    setItems((cur) => cur.filter((i) => !keys.has(keyOf(i.unit_id, i.locale))));
    try {
      await Promise.all(card.parts.map((p) => dropItem(batchId, p.unit_id, p.locale)));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }, [batchId]);

  const removeItem = useCallback(async (item: BatchItem) => {
    if (!batchId) return;
    setItems((cur) => cur.filter((i) => keyOf(i.unit_id, i.locale) !== keyOf(item.unit_id, item.locale)));
    await dropItem(batchId, item.unit_id, item.locale);
  }, [batchId]);

  const clearBatch = useCallback(async () => {
    if (!batchId) return;
    const snapshot = items;
    setItems([]);
    await Promise.all(snapshot.map((i) => dropItem(batchId, i.unit_id, i.locale)));
  }, [batchId, items]);

  /** Sends the cart and opens a fresh one. Returns how many items went. */
  const send = useCallback(async (note: string | null) => {
    if (!batchId) return 0;
    const n = items.length;
    await submitBatch(batchId, note);
    setItems([]);
    setBatchId(await getOrCreateDraft(memberId));
    return n;
  }, [batchId, items.length, memberId]);

  return { batchId, items, decisions, decide, undo, removeItem, clearBatch, send, error, setError };
}
